import { formatEther, parseEther } from 'ethers/lib/utils';
import { UserPositionRaw } from '../../../../../hooks/web3/trade/useUserPositions';
import EditPositionPreview from '../EditPositionPreview';
import { useMemo } from 'react';
import useUserFee from '../../../../../hooks/web3/useUserFee';
import { calculatePnl } from '../../../../../helpers';
import useTokenStats from '../../../../../hooks/web3/useTokenStats';
import { toBigNumber } from '../../../../../web3/utils';

type AdjustPreviewProps = {
    userPosition: UserPositionRaw | undefined;
    leverage: string;
    isInitialLeverage: boolean;
};

const AdjustPreview: React.FC<AdjustPreviewProps> = ({
    userPosition,
    leverage,
    isInitialLeverage,
}) => {
    const tokenStats = useTokenStats();
    const { userFee } = useUserFee();

    const pnlValues = userPosition
        ? calculatePnl(
              userPosition,
              tokenStats?.[userPosition.tradePair.chartSymbol]?.price
          )
        : undefined;

    // use toBigNumber here incase entryPrice is used which is in 24 decimals.
    const markPrice = useMemo(
        () =>
            tokenStats && userPosition && userPosition.tradePair
                ? toBigNumber(
                      tokenStats?.[userPosition.tradePair.chartSymbol]?.price ||
                          userPosition.entryPrice ||
                          '0'
                  )
                : toBigNumber(userPosition?.entryPrice || '0'),
        [tokenStats, userPosition]
    );

    const addedSize = useMemo(() => {
        let size = parseEther(userPosition?.margin || '1')
            .mul(parseEther(leverage || '1'))
            .sub(parseEther(userPosition?.volume || '1').mul(parseEther('1')))
            .div(
                parseEther('1').add(
                    parseEther(leverage || '1')
                        .mul(userFee || 0)
                        .div(100_00)
                )
            );

        if (size.lt(0) || isInitialLeverage) {
            size = parseEther('0');
        }

        return size;
    }, [
        userPosition?.margin,
        userPosition?.volume,
        leverage,
        userFee,
        isInitialLeverage,
    ]);

    const openFee = useMemo(
        () => addedSize.mul(userFee || 0).div(100_00),
        [addedSize, userFee]
    );

    const previewCollateral = useMemo(
        () => parseEther(userPosition?.margin || '1').sub(openFee),
        [userPosition?.margin, openFee]
    );

    const previewSize = useMemo(
        () => parseEther(userPosition?.volume || '0').add(addedSize),
        [userPosition?.volume, addedSize]
    );

    const previewEntryPrice = useMemo(
        () =>
            tokenStats && userPosition && userPosition.tradePair
                ? previewSize
                      .mul(parseEther('1'))
                      .div(
                          parseEther(userPosition.size || '0').add(
                              addedSize
                                  .mul(parseEther('1'))
                                  .div(
                                      parseEther(
                                          tokenStats?.[
                                              userPosition.tradePair?.chartSymbol
                                          ]?.price || '1'
                                      )
                                  )
                          )
                      )
                : parseEther('0'),
        [previewSize, userPosition, tokenStats, addedSize]
    );

    const previewBankruptcyPrice = useMemo(
        () =>
            previewEntryPrice
                .mul(
                    parseEther('1')[userPosition?.isShort ? 'add' : 'sub'](
                        parseEther('1')
                            .mul(parseEther('1'))
                            .div(parseEther(leverage || '1'))
                    )
                )
                .div(parseEther('1')),
        [previewEntryPrice, leverage, userPosition?.isShort]
    );

    return (
        <EditPositionPreview
            positionDetails={{
                tradePair: userPosition?.tradePair,
                bankruptcyPrice: {
                    value: userPosition?.bankruptcyPrice || '0',
                    previewValue: addedSize.gt(0)
                        ? formatEther(previewBankruptcyPrice)
                        : undefined,
                },
                collateral: {
                    value: userPosition?.margin || '0',
                    previewValue: addedSize.gt(0)
                        ? formatEther(previewCollateral)
                        : undefined,
                },
                entryPrice: {
                    value: userPosition?.entryPrice || '0',
                    previewValue: addedSize.gt(0)
                        ? formatEther(previewEntryPrice)
                        : undefined,
                },
                fees: {
                    value: undefined,
                    previewValue: addedSize.gt(0) ? formatEther(openFee) : undefined,
                },
                isShort: userPosition?.isShort ?? false, // should never happen, but just in case
                leverage: {
                    value: userPosition?.leverage || '0',
                    previewValue: addedSize.gt(0) ? leverage : undefined,
                },
                markPrice: {
                    value: formatEther(markPrice) || '0',
                },
                pnl: {
                    value: pnlValues?.pnl,
                },
                size: {
                    value: userPosition?.volume || '0',
                    previewValue: addedSize.gt(0) ? formatEther(previewSize) : undefined,
                },
            }}
        />
    );
};

export default AdjustPreview;
